/**
 * FIFO Allocation View (content only, no Dialog wrapper).
 *
 * Per-part timeline of StockPOAllocation events: which PO each upload was
 * FIFO-allocated to, when an ASN consumed it, and when an ASN cancel
 * restored it. Used both standalone in FIFOAllocationDrawer (Change History
 * sub-tab) and embedded as a tab inside StockInputHistoryDrawer (Current
 * Stock per-part drawer).
 */
import { useEffect, useState, useCallback } from 'react';
import { Loader2, PackagePlus, Truck, Undo2 } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';

interface FIFOAllocationViewProps {
  /** Part number to fetch events for. */
  partNumber: string;
  /** When false, the view stays idle (no fetch). Lets the parent control
   *  fetch timing when this view sits inside a tabbed container. */
  active?: boolean;
}

interface Event {
  id: number;
  event_type: 'allocate' | 'consume' | 'restore';
  qty: string;
  po_number: string | null;
  po_line_number: number | null;
  po_line_id: number | null;
  asn_number: string | null;
  created_at: string;
}

function eventChip(type: Event['event_type']) {
  switch (type) {
    case 'allocate':
      return { label: 'Allocate', Icon: PackagePlus, className: 'bg-blue-100 text-blue-800', dotClass: 'bg-blue-500' };
    case 'consume':
      return { label: 'Consume', Icon: Truck, className: 'bg-red-100 text-red-800', dotClass: 'bg-red-500' };
    case 'restore':
      return { label: 'Restore', Icon: Undo2, className: 'bg-emerald-100 text-emerald-800', dotClass: 'bg-emerald-500' };
  }
}

export default function FIFOAllocationView({ partNumber, active = true }: FIFOAllocationViewProps) {
  const [items, setItems] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const { formatDateTime } = useFormatters();
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    if (!partNumber) return;
    setLoading(true);
    setError(null);
    try {
      const res = await callOffAPI.getAllocationHistory(partNumber);
      setItems(res.data.items || []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load allocation history');
    } finally {
      setLoading(false);
    }
  }, [partNumber]);

  useEffect(() => { if (active) fetchEvents(); }, [active, fetchEvents]);

  // Net allocation per PO line — recomputed client-side from the event
  // stream so the view can show "where your stock currently sits"
  // alongside the raw timeline.
  const netByLine = items.reduce<Record<string, { po: string; line: number | null; net: number }>>(
    (acc, ev) => {
      if (!ev.po_line_id) return acc;
      const key = String(ev.po_line_id);
      if (!acc[key]) acc[key] = { po: ev.po_number || '—', line: ev.po_line_number, net: 0 };
      const qty = parseFloat(ev.qty) || 0;
      acc[key].net += ev.event_type === 'consume' ? -qty : qty;
      return acc;
    }, {}
  );
  const netRows = Object.values(netByLine).filter(r => r.net > 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return <div className="text-sm text-red-600 py-8 text-center">{error}</div>;
  }
  if (items.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-8 text-center">
        No allocation events for this part yet.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {netRows.length > 0 && (
        <div className="rounded-md border bg-muted/30 p-3 text-sm">
          <div className="font-medium text-muted-foreground mb-2">Currently held against:</div>
          <ul className="space-y-1">
            {netRows.map(r => (
              <li key={`${r.po}-${r.line}`} className="flex items-center justify-between font-mono text-xs">
                <span>PO {r.po} · line {r.line ?? '—'}</span>
                <span className="font-semibold text-blue-700">{r.net.toLocaleString()}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ol className="relative border-l border-muted-foreground/20 pl-4 space-y-3">
        {items.map(ev => {
          const chip = eventChip(ev.event_type);
          return (
            <li key={ev.id} className="relative">
              <span className={`absolute -left-[19px] top-2 inline-block h-3 w-3 rounded-full ${chip.dotClass} ring-2 ring-background`} />
              <div className="rounded-md border bg-card p-3 text-sm space-y-1">
                <div className="flex items-center justify-between gap-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${chip.className}`}>
                    <chip.Icon className="h-3 w-3" />
                    {chip.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDateTime(ev.created_at)}
                  </span>
                </div>
                <div className="font-mono text-xs flex flex-wrap items-center gap-x-4 gap-y-1">
                  <span>PO {ev.po_number || '—'} · line {ev.po_line_number ?? '—'}</span>
                  <span>qty <span className="font-semibold">{parseFloat(ev.qty).toLocaleString()}</span></span>
                  {ev.asn_number && <span>ASN {ev.asn_number}</span>}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
